import { FC, useContext } from 'react';
import { AllieSpinner, AllieText } from '../../../../components';
import {
  QuickButtonContainer,
  StyledEmptyStateCTA,
  StyledLastAssignedReasonsContainer,
  StyledSpinnerWrapper,
  StyledStopTypeContainer,
} from '../AssignReasonForm.styles';
import { LastReasonsAssign } from '../AssignReasonForm.types';
import { AssignReasonFormContext } from '../AssignReasonForm.context';
import { QuickButton } from '../QuickButton';
import { useWizardContext } from '../../../../components/Wizard';
import { StopTypes } from '../AssignReasons.enum';
import { StopTypeChipsRow } from '../StopTypeChipsRow';

interface Step0Props {
  onSelectStopReason: (reason: LastReasonsAssign) => void;
  lastReasonsAssign?: LastReasonsAssign[];
  labels: {
    selectReason: string;
    planned: string;
    unplanned: string;
    all: string;
    title: string;
    emptyState: string;
    emptyStateCTA: string;
  };
}

const LastReasonsList: FC<Step0Props & { onChooseManually: () => void }> = ({
  lastReasonsAssign,
  labels,
  onSelectStopReason,
  onChooseManually,
}) => {
  if (!lastReasonsAssign) {
    return (
      <StyledSpinnerWrapper>
        <AllieSpinner color={'primary'}></AllieSpinner>
      </StyledSpinnerWrapper>
    );
  }

  if (!lastReasonsAssign.length) {
    return (
      <StyledSpinnerWrapper>
        <AllieText type="body1">{labels.emptyState}</AllieText>
        {labels.emptyStateCTA && (
          <StyledEmptyStateCTA onClick={onChooseManually}>
            {labels.emptyStateCTA}
          </StyledEmptyStateCTA>
        )}
      </StyledSpinnerWrapper>
    );
  }

  const stopTypeLabel: Record<string, string> = {
    planned: labels.planned,
    unplanned: labels.unplanned,
  };

  const formatText = (values: (string | null | undefined)[]) => {
    return values.filter((value) => value != null && value !== '').join(' > ');
  };

  return (
    <QuickButtonContainer>
      {lastReasonsAssign.map((stopReason, index) => {
        return (
          <QuickButton
            key={index + stopReason.reasonValue}
            title={formatText([
              stopReason.lastAssignedMachineValue,
              stopReason.reasonValue,
              stopReason.failureModes?.[0]?.name,
            ])}
            subtitle={formatText([stopTypeLabel[stopReason.stopType], stopReason.categoryValue])}
            onClick={() => onSelectStopReason(stopReason)}
            type="full"
          />
        );
      })}
    </QuickButtonContainer>
  );
};

export const Step0: FC<Step0Props> = ({ labels, ...props }) => {
  const { stepData, setStepData } = useContext(AssignReasonFormContext);
  const { goNextPage } = useWizardContext();

  const quickFilter = (stepData.step1?.typeStop ?? StopTypes.all) as StopTypes;

  const handleTypeChange = (type: StopTypes) => {
    setStepData({
      ...stepData,
      step1: { ...stepData.step1, typeStop: type },
    });
  };

  return (
    <div>
      <StyledStopTypeContainer>
        <AllieText type="body2" color="neutral-50">
          {labels.title}
        </AllieText>
        <StopTypeChipsRow
          selectedType={quickFilter}
          onTypeChange={handleTypeChange}
          labels={labels}
        />
      </StyledStopTypeContainer>
      <StyledLastAssignedReasonsContainer>
        <AllieText type="body2" color="neutral-50">
          {labels.selectReason}
        </AllieText>
        <LastReasonsList labels={labels} onChooseManually={goNextPage} {...props} />
      </StyledLastAssignedReasonsContainer>
    </div>
  );
};
